import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { SearchInput } from 'app/common/presentation'
import { useConnections } from 'app/connections/application'
import { ConnectionCategory } from 'app/connections/types'

import { Badge, Tabs } from 'components'

import { mapToUrl } from '../../connectionUrlMapper'
import styles from './ConnectionBrowser.module.scss'
import ConnectionCard from './ConnectionCard'

type Filter = {
  category: ConnectionCategory | 'all'
  count: number
  label: string
}

const ConnectionBrowser = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('connections')
  const { availableConnections } = useConnections()

  const [search, setSearch] = useState('')

  const enrichedAvailableConnections = useMemo(
    () =>
      availableConnections.map((availableConnection) => {
        const description = t(`available.${availableConnection.name}.description`)
        return {
          ...availableConnection,
          description: description.substring(0, description.indexOf('.') + 1) ?? '',
          displayName: t(`available.${availableConnection.name}.displayName`),
        }
      }),
    [availableConnections, t],
  )

  const filteredEnrichedAvailableConnections = useMemo(
    () =>
      enrichedAvailableConnections.filter((enrichedAvailableConnection) =>
        enrichedAvailableConnection.displayName.toLowerCase().includes(search.toLowerCase()),
      ),
    [enrichedAvailableConnections, search],
  )

  const categoryFilters: Filter[] = useMemo(
    () => [
      {
        category: 'all',
        count: filteredEnrichedAvailableConnections.length,
        label: t('overview.browse.filter.all'),
      },
      ...filteredEnrichedAvailableConnections.reduce((acc, current) => {
        const existingCategoryEntry = acc.find((entry) => entry.category === current.category)

        if (existingCategoryEntry) {
          existingCategoryEntry.count += 1
        } else {
          acc.push({
            category: current.category,
            count: 1,
            label: t(`category.${current.category}`),
          })
        }

        return acc
      }, [] as Filter[]),
    ],
    [filteredEnrichedAvailableConnections, t],
  )

  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(categoryFilters[0])

  useEffect(() => setSelectedCategoryFilter(categoryFilters[0]), [categoryFilters])

  return (
    <>
      <div className={styles.toolbar}>
        <Tabs
          className={styles.filter}
          data={categoryFilters}
          onTabSelect={setSelectedCategoryFilter}
          renderContent={(categoryFilter, selected) => (
            <div className={styles.tab}>
              {categoryFilter.label}
              <Badge mint={selected} value={categoryFilter.count} />
            </div>
          )}
          selected={selectedCategoryFilter}
        />
        <div className={styles.filter}></div>
        <SearchInput
          className={styles.search}
          onChange={setSearch}
          placeholder={t('overview.browse.search')}
          value={search}
        />
      </div>
      <div className={styles.connections}>
        {filteredEnrichedAvailableConnections
          .filter(
            (filteredConn) =>
              selectedCategoryFilter.category === 'all' ||
              filteredConn.category === selectedCategoryFilter.category,
          )
          .map((filteredEnrichedAvailableConnection, index) => (
            <ConnectionCard
              key={index}
              onClick={() => navigate(`${mapToUrl(filteredEnrichedAvailableConnection.name)}`)}
              connection={filteredEnrichedAvailableConnection}
            />
          ))}
      </div>
    </>
  )
}

export default ConnectionBrowser
